/**
 * Async narration, the mock-timeline mount for the step ledger. Screen spec
 * 4.9, Instrument motif 5. See StepLedger.tsx for the pure list itself.
 *
 * Progress is clock derived, from lib/mock/simulate. Nothing here increments
 * a counter, so a refresh mid build keeps the build correct and a
 * backgrounded tab does not desync.
 */

import type { ReactNode } from 'react';
import { useTicker } from '../../lib/hooks/use-ticker';
import { durationSeconds } from '../../lib/format';
import { t } from '../../lib/i18n/t';
import { scriptFor, stepOffsets } from '../../lib/mock/scripts';
import { stateOf } from '../../lib/mock/simulate';
import type { Timeline } from '../../lib/types';
import { LONG_RUNNING_MS, StepLedger } from './StepLedger';
import type { NarrationStep } from './StepLedger';

export { LONG_RUNNING_MS, StepLedger };
export type { NarrationStep, StepLedgerProps } from './StepLedger';

export interface NarratedLineProps {
  /** The substance of the waiting state: Uploading leek-stew.jpg */
  sentence: string;
  /** Off once the operation lands, so the track stops moving. */
  running?: boolean;
}

/** The single narrated line, for one step operations. Screen spec 4.9b. */
export function NarratedLine({ sentence, running = true }: NarratedLineProps) {
  return (
    <div className="narrline" role="status" aria-live="polite">
      <p className="narrline__say">{sentence}</p>
      <div className="narrline__track" data-running={running ? 'yes' : 'no'}>
        <span className="narrline__bar" />
      </div>
    </div>
  );
}

export interface AsyncNarrationProps {
  timeline: Timeline;
  /** Rendered under the list when the run failed. */
  error?: ReactNode;
  onLeave?: () => void;
  /**
   * Shows the leave this page line without waiting ninety real seconds. The
   * slow state is one of the states S5 has to be able to show a reviewer, and
   * at any speed the demo offers, the run lands long before the real clock
   * gets there. Product callers leave this alone.
   */
  forceLongRunning?: boolean;
}

/** The engine driven mount: reads the one ticker and the one clock. */
export function AsyncNarration({
  timeline,
  error,
  onLeave,
  forceLongRunning = false,
}: AsyncNarrationProps) {
  const now = useTicker();
  const script = scriptFor(timeline.scriptId);
  const offsets = stepOffsets(script);
  const state = stateOf(timeline, now);

  const steps: NarrationStep[] = script.steps.map((step, index) => {
    const status = state.stepStatuses[index];
    const start = index === 0 ? 0 : offsets[index - 1];

    // A failed step reports its time too. "Building your app, 16s" is the
    // sentence that tells a person the build ran and then broke, rather than
    // never started, and it is the number the build log's last line repeats.
    let elapsed: string | undefined;
    if (status === 'succeeded') elapsed = durationSeconds(step.ms);
    else if (status === 'running' || status === 'failed') {
      elapsed = durationSeconds(Math.max(0, state.elapsedMs - start));
    }

    return {
      label: t(step.label),
      status,
      elapsed,
      // Spec 4.9 hangs the mono detail on a step that finished. A step that is
      // still running must not report what it is about to find.
      detail: status === 'succeeded' ? step.detail : undefined,
    };
  });

  const longRunning =
    forceLongRunning || (now - timeline.startedAt >= LONG_RUNNING_MS && state.status === 'running');

  return (
    <StepLedger
      steps={steps}
      error={state.status === 'failed' ? error : undefined}
      longRunning={longRunning}
      onLeave={onLeave}
    />
  );
}
